import { useEffect, useState } from "react";
import { Check, Users2 } from "lucide-react";
import { API } from "../App";

/**
 * Multi-select pill-checkbox for customer segments.
 * Pass the current array of segment_ids and an onChange callback.
 * Renders nothing when no segments exist in the DB (graceful empty state).
 */
export default function SegmentMultiSelect({
  value = [],
  onChange,
  label = "Visible to Customer Segments",
  helperText = "Leave empty to show to all customers.",
  testIdPrefix = "segment-select",
}) {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/admin/segments`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!alive) return;
        setSegments(Array.isArray(data) ? data : []);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const toggle = (segmentId) => {
    const set = new Set(value || []);
    if (set.has(segmentId)) set.delete(segmentId);
    else set.add(segmentId);
    onChange(Array.from(set));
  };

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-wrapper`}>
      <div className="flex items-center gap-1.5">
        <Users2 size={13} className="text-gray-500" />
        <label className="text-sm font-medium text-gray-700">{label}</label>
      </div>
      {loading ? (
        <div className="text-[11px] text-gray-400">Loading segments…</div>
      ) : segments.length === 0 ? (
        <div className="text-[11px] text-gray-400 italic bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
          No customer segments defined yet. Create some under{" "}
          <span className="font-medium">Admin → Customer Segments</span> to restrict visibility.
        </div>
      ) : (
        <div
          className="flex flex-wrap gap-1.5"
          data-testid={`${testIdPrefix}-options`}
        >
          {segments.map((s) => {
            const active = (value || []).includes(s.segment_id);
            return (
              <button
                type="button"
                key={s.segment_id}
                onClick={() => toggle(s.segment_id)}
                className={`group inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
                  active
                    ? "bg-green-600 text-white border-green-600 shadow-sm"
                    : "bg-white text-gray-600 border-gray-200 hover:border-green-400 hover:text-green-700"
                }`}
                data-testid={`${testIdPrefix}-option-${s.segment_id}`}
              >
                {active && <Check size={12} />}
                {s.name}
              </button>
            );
          })}
        </div>
      )}
      {helperText && segments.length > 0 && (
        <p className="text-[10px] text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
